import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Stack, Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import HeadTitle from '../../components/HeadTitle';
import Loader from '../../components/common/Loader/Loader';
import UserProfileMainPage from '../../components/user-profile-main/UserProfileMainPage';
import { getRequest } from '../../utils/axiosUtils';
import { ACTIVE_STATUSES, STATUS_DESCRIPTION, STATUS_LABEL, STATUS_ORDER, statusLabel } from '../../utils/memberStatus';

/**
 * Online Right Now — a full page (route `/online-now`), opened in a new tab from the
 * "Online now" KPI on the Daily Actives page. Same pattern as the Ghost Users card →
 * `/onboarding-dropoff`: it renders inside the admin Layout (sidebar + header), not as a
 * modal. It replaces the OnlineUsersModal that used to live in daily-actives-container.
 *
 * Data — ONE call: GET /admin/attribution/actives-online-users -> { total, shown, capped,
 * users[] }, sorted newest-connect-first, bots excluded server-side (CANON_BASE — ~9.7k
 * bots carry isOnLine:true from the bot rotation). `total` also feeds the header count, so
 * there is nothing to reconcile between two endpoints. Re-polls every 45s.
 *
 * NO platform filter, by design decision: the mock doesn't have one. Note the KPI that
 * links here IS platform-filtered, so opening this page from a filtered KPI lists more
 * people than the number clicked. Accepted trade for mock fidelity — the Device column
 * still shows each member's platform.
 *
 * WHAT "ONLINE" MEANS HERE — read this before trusting the number. It is the account's
 * `isOnLine` flag in the database, verbatim (user's explicit call), which is what the rest
 * of the admin and the app read. That flag is set on socket connect and cleared on
 * disconnect. An unclean disconnect (server restart, dropped socket) never fires the clear,
 * so the flag sticks true forever. Measured live 2026-07-17: of 81 listed, only 7 had
 * connected within 15 minutes and 62 had connected over 30 days ago — and for 79 of them no
 * `offline` log row was ever written. So MOST OF THIS LIST IS NOT REALLY ONLINE.
 *
 * The presence dot USED to be driven off a 15-minute recency check for exactly that reason.
 * It is now always green (user's call, 2026-07-28): the page reports the flag, so it should
 * not list a member as online and then dot them "probably not really on". The recency signal
 * is not gone, it moved into the dot's tooltip — see lastSeenLabel. Note the server-side 24h
 * freshness gate was dropped the same day (AttributionController onlineMatch), so stale rows
 * reach this page more often than the 2026-07-17 measurement above would suggest.
 *
 * The mock's legend claims an "activity ping within the last 15 minutes" (there is no such
 * ping), that deleted/suspended/deactivated "cannot be online" (a suspended member is
 * online right now, precisely because flags stick), that Ghost means "never sent a message"
 * and that Hidden means "Private Mode" — all four are wrong for this data, so the legend
 * here is rewritten against the canonical state machine (helpers/statsFilters.js):
 * Approved = on search right now; Hidden = complete but not on search (admin, AI Shortlist,
 * shadow hide, the member, or visibility off); Inactive = was complete, photo removed or
 * below 100%; Ghost = signup not finished. Names/meanings come from utils/memberStatus.js.
 *
 * Scoped under `.favor-on` so its styles never touch the admin's global CSS. Fonts use the
 * admin app's own families (ivypresto-headline serif + indivisible sans).
 */

const ONLINE_POLL_MS = 45000;

const GENDERS = [['all', 'All'], ['women', 'Women'], ['men', 'Men']];
// The seven canonical statuses, plus "Active" — not a status, the Approved + Hidden total —
// as a convenience pill that narrows to both.
const STATUSES = [
  ['all', 'All'], ['active', STATUS_LABEL.active],
  ...STATUS_ORDER.map((k) => [k, STATUS_LABEL[k]]),
];

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

// The presence dot is ALWAYS GREEN (user's call, 2026-07-28). It used to go grey unless
// the socket had connected within 15 minutes, which meant the page argued with itself: a
// row was listed as online and then dotted "probably not really on". Membership in this
// list is the platform's only definition of online — the same `isOnLine` flag the app
// itself reads — so the dot now states it instead of hedging.
//
// The hedge was not baseless, and nothing about the DATA changed when the dot did:
// `lastActive` is written only on socket connect/disconnect (socketUtils
// handleUpdateUserCurrentStatus — there is no heartbeat), so an unclean disconnect leaves
// `isOnLine` stuck true forever with `lastActive` frozen at the last real connect. Those
// rows now read as fully online. The age of the last presence event is the only signal
// that ever distinguished them, so it survives in the dot's tooltip and the row title —
// a row whose last connect was days ago is very likely a stuck flag, not a long session.
const lastSeenLabel = (iso) => {
  if (!iso) return 'Online · no connect event recorded';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'Online · connected just now';
  if (mins < 60) return `Online · connected ${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `Online · connected ${hrs}h ago`;
  return `Online · connected ${Math.round(hrs / 24)}d ago — flag may be stale`;
};

const initialsOf = (name) =>
  (String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => (w[0] || '').toUpperCase()).join('') || 'M');

// The mock's avatar palette, kept because the roster reads as a wall of identical tiles
// without it. Indexed by a stable hash of the user id so a member keeps the same colour
// across refreshes (an index-based pick would reshuffle every poll as the order changes).
const PAL = [
  'linear-gradient(150deg,#33418f,#1e2a66)', 'linear-gradient(150deg,#96613d,#6e4426)',
  'linear-gradient(150deg,#6f4da0,#4d3374)', 'linear-gradient(150deg,#3f7d5f,#2c5a43)',
  'linear-gradient(150deg,#8f3f5f,#672a44)', 'linear-gradient(150deg,#3f6f8f,#2b4f68)',
  'linear-gradient(150deg,#8f6a2f,#67491d)', 'linear-gradient(150deg,#5f5f8f,#414166)',
];
const gradOf = (id) => {
  const s = String(id || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h + s.charCodeAt(i)) % PAL.length;
  return PAL[h];
};

// IST, because every other admin surface counts on the Asia/Kolkata calendar.
const istClock = (d) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
const istDate = (d) => d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata' });

const Pills = ({ items, value, onChange }) => (
  <div className="periods">
    {items.map(([k, label]) => (
      <button key={k} type="button" className={value === k ? 'on' : ''} onClick={() => onChange(k)}>{label}</button>
    ))}
  </div>
);

const OnlineNow = () => {
  const navigate = useNavigate();
  const [gender, setGender] = useState('all');
  const [status, setStatus] = useState('all');

  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = useCallback(async (quiet) => {
    if (!quiet) { setLoading(true); setError(false); }
    try {
      const res = await getRequest({ url: '/admin/attribution/actives-online-users' });
      // getRequest returns null when a superseding call aborted this one. A cancel is not a
      // failure — leave the last good list up rather than flashing an error over it.
      if (!res) return;
      const body = res?.data;
      const payload = Array.isArray(body?.users) ? body : body?.data;
      if (payload && Array.isArray(payload.users)) { setRows(payload); setRefreshedAt(new Date()); }
      else if (!quiet) setError(true);
    } catch (e) {
      if (!quiet) setError(true);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
    // Quiet poll: swaps data in without touching the spinner, so a background refresh never
    // blanks the list the user is reading.
    const id = setInterval(() => load(true), ONLINE_POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const users = useMemo(() => rows?.users || [], [rows]);
  const total = rows?.total ?? 0;

  // Both filters only ever NARROW what is already loaded, so they stay client-side —
  // instant, and they can't desync the table from its own header count.
  const shown = useMemo(() => users.filter((u) => {
    if (gender === 'women' && u.gender !== 'Female') return false;
    if (gender === 'men' && u.gender !== 'Male') return false;
    if (status === 'active') { if (!ACTIVE_STATUSES.includes(u.acct)) return false; }
    else if (status !== 'all' && u.acct !== status) return false;
    return true;
  }), [users, gender, status]);

  const now = refreshedAt || new Date();

  return (
    <Box sx={{ pb: 4 }}>
      <style>{STYLES}</style>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <HeadTitle
          imageSrc="/assets/images/admin-panel/analytics.png"
          text="Online Right Now"
          description="Live presence across the platform — every member the database currently flags as online"
        />
        <button type="button" className="on-back" onClick={() => navigate('/daily-actives')}>
          ← Daily Actives
        </button>
      </Stack>

      <div className="favor-on">
        {loading ? (
          <div className="on-center"><Loader /></div>
        ) : error ? (
          <div className="ctx" style={{ padding: '40px 0' }}>Couldn’t load who’s online. Try again in a moment.</div>
        ) : (
          <>
            <div className="mhead">
              <div className="hsub">
                <b>{fmt(total)}</b> {total === 1 ? 'member is' : 'members are'} online
                {/* The cap is a safety valve that shouldn't bite at real presence volumes,
                    but if it does, say so rather than letting the table's length read as
                    the total. */}
                {rows?.capped ? <> · showing the {fmt(rows.shown)} most recently active</> : null}
                {' '}· bots and staff accounts excluded
              </div>
              <span className="refch">AUTO-REFRESH · {Math.round(ONLINE_POLL_MS / 1000)} S</span>
            </div>
            <div className="ctx">{istDate(now)}, {istClock(now)} IST · last refreshed {istClock(now)}</div>

            <div className="panel">
              <div className="phead">
                <h2>Members Online Now</h2>
                <span className="pmeta">
                  {fmt(shown.length)} OF {fmt(users.length)} ONLINE MEMBERS SHOWN · REFRESHES EVERY {Math.round(ONLINE_POLL_MS / 1000)} S
                </span>
              </div>

              <div className="sfilters">
                <span className="fl">Gender</span><Pills items={GENDERS} value={gender} onChange={setGender} />
                <span className="fl">Status</span><Pills items={STATUSES} value={status} onChange={setStatus} />
              </div>

              <div className="tblwrap">
                <table>
                  <thead>
                    <tr>
                      <th>#</th><th>Member</th><th>Location</th><th>Gender</th><th>Account status</th><th>Device</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!shown.length ? (
                      <tr><td colSpan="6"><div className="empty">No members match the current filters.</div></td></tr>
                    ) : shown.map((u, i) => {
                      const woman = u.gender === 'Female';
                      const man = u.gender === 'Male';
                      return (
                        <tr key={u.id} onClick={() => setProfileUserId(u.id)} title="Open profile">
                          <td className="sn">{i + 1}</td>
                          <td>
                            <div className="mem">
                              <span className="avw">
                                <span className="av" style={{ background: gradOf(u.id) }}>
                                  {u.profilePicture
                                    ? <img src={u.profilePicture} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                    : initialsOf(u.name || u.userName)}
                                </span>
                                {/* Always green: every row on this page IS online by the only
                                    definition the platform has (the isOnLine flag), so the dot
                                    states that rather than second-guessing it. The last presence
                                    event moves into the tooltip — see lastSeenLabel above. */}
                                <span className="pdot on" title={lastSeenLabel(u.lastActive)} />
                              </span>
                              <div className="mi">
                                <b>{u.name || u.userName || 'Member'}</b>
                                {/* Only ~4% of online members are subscribers, so this is a
                                    rare, high-signal marker rather than a mostly-empty column. */}
                                {u.subscribed ? <span className="paid">PAID</span> : null}
                                <small>{u.userName ? `@${u.userName}` : (u.email || '—')}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            {/* City is ~93% populated and country ~100% — fall back to the
                                coarser field rather than printing a dangling separator. */}
                            {u.city
                              ? <><b>{u.city}</b> <span className="mut">· {u.country}</span></>
                              : u.country ? <b>{u.country}</b> : <span className="mut">Unknown</span>}
                          </td>
                          <td>
                            {woman || man
                              ? <span className={`ch ${woman ? 'gf' : 'gm'}`}>{woman ? 'WOMAN' : 'MAN'}</span>
                              : <span className="ch gu">UNKNOWN</span>}
                          </td>
                          <td>{u.acct ? <span className={`ch s-${u.acct}`} title={STATUS_DESCRIPTION[u.acct]}>{statusLabel(u.acct).toUpperCase()}</span> : <span className="mut">—</span>}</td>
                          <td className="dev">{u.platform === 'app' ? 'App' : 'Web'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="legend">
                Everyone listed carries the account’s live <b>isOnLine</b> flag in the database · <b>Approved</b> = on
                search right now · <b>Hidden</b> = complete but not on search · <b>Inactive</b> = was complete, photo
                removed or below 100% · <b>Ghost</b> = signup not finished · <b>Active</b> = Approved + Hidden. Click
                any row to open the member’s profile.
                <br />
                <b>Everyone here shows a green dot — this page reports the <b>isOnLine</b> flag exactly as the app
                does.</b> Hover a member’s dot to see when their socket last connected. That timestamp is only written
                on connect and disconnect, so a long live session can show an old time; equally, a disconnect the
                server never records (restart, dropped socket) leaves <b>isOnLine</b> stuck true, so a member whose
                last connect was days ago is probably not really on. It is also why suspended or deactivated accounts
                can appear here.
              </div>
            </div>
          </>
        )}

        <Modal open={!!profileUserId} onClose={() => setProfileUserId(null)}>
          <Box sx={profileModalStyle}>
            <Box sx={{ position: 'relative', height: 44 }}>
              <IconButton sx={{ position: 'absolute', top: 10, right: 0 }} onClick={() => setProfileUserId(null)}>
                <CloseIcon />
              </IconButton>
            </Box>
            {profileUserId && <UserProfileMainPage userId={profileUserId} />}
          </Box>
        </Modal>
      </div>
    </Box>
  );
};

const profileModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: 1300,
  width: '95%',
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: 24,
  p: '0 30px 30px 30px',
  maxHeight: '80vh',
  overflow: 'auto',
};

const STYLES = `
.on-back{border:1px solid #e3e3e3;background:#fff;font-family:"indivisible","Helvetica Neue",Arial,sans-serif;font-size:13px;font-weight:600;color:#3f4854;padding:9px 15px;border-radius:10px;cursor:pointer;white-space:nowrap;flex-shrink:0;transition:.12s}
.on-back:hover{background:#f4f2ef;color:#16161a}
.favor-on{--ink:#25313f;--ink-2:#3f4854;--muted:#7c8590;--faint:#9aa2ac;--line:#ececec;--line-2:#e3e3e3;--wash:#f4f2ef;--head:#faf8f5;
  --green:#2f8f5b;--green-soft:#eaf6ef;--blue:#3f6fd8;--blue-soft:#eef4fe;--red:#cf4233;--red-soft:#fdecea;
  --amber:#8a6d1f;--amber-soft:#fdf6e6;--rose:#b23c7e;--rose-soft:#fdf3f8;--violet:#7c3aed;--violet-soft:#f3eefe;--black:#14181c;
  --serif:"ivypresto-headline",Georgia,serif;--sans:"indivisible","Neue Montreal","Helvetica Neue",Arial,sans-serif;
  --card-sh:0 1px 2px rgba(20,25,35,.04),0 6px 18px -10px rgba(20,25,35,.10);
  color:var(--ink-2);font-family:var(--sans);font-size:14px;line-height:1.5;-webkit-font-smoothing:antialiased}
.favor-on *{box-sizing:border-box;margin:0;padding:0}
.favor-on .on-center{min-height:360px;display:flex;align-items:center;justify-content:center}
.favor-on .mhead{display:flex;justify-content:space-between;align-items:flex-end;gap:18px;flex-wrap:wrap;margin:8px 0 4px}
.favor-on .hsub{font-size:14px;color:var(--muted)}
.favor-on .hsub b{font-size:16px;color:var(--ink);font-weight:700}
.favor-on .refch{font-size:11px;font-weight:700;letter-spacing:.06em;background:var(--green-soft);color:var(--green);border-radius:999px;padding:6px 13px;white-space:nowrap}
.favor-on .ctx{font-size:12px;color:var(--faint);margin:10px 0 14px}
.favor-on .panel{background:#fff;border:1px solid var(--line);border-radius:16px;padding:20px 22px;box-shadow:var(--card-sh)}
.favor-on .phead{display:flex;justify-content:space-between;align-items:center;gap:14px;flex-wrap:wrap}
.favor-on .phead h2{font-family:var(--sans);font-weight:600;font-size:24px !important;line-height:1.3;color:var(--ink)}
.favor-on .pmeta{font-size:10.5px;letter-spacing:.09em;color:var(--faint);font-weight:700}
.favor-on .sfilters{display:flex;gap:12px;align-items:center;flex-wrap:wrap;margin:14px 0 16px}
.favor-on .fl{font-size:10.5px;letter-spacing:.12em;text-transform:uppercase;font-weight:700;color:var(--faint)}
.favor-on .periods{display:flex;gap:3px;background:var(--head);border:1px solid var(--line);border-radius:10px;padding:3px;flex-wrap:wrap}
.favor-on .periods button{border:0;background:transparent;padding:6px 11px;border-radius:7px;font-family:inherit;font-size:11.5px;font-weight:600;color:var(--ink-2);cursor:pointer;white-space:nowrap;transition:.12s}
.favor-on .periods button:hover{color:var(--ink)}
.favor-on .periods button.on{background:var(--black);color:#fff}

/* Sticky header, mechanism A (page-scroll): the table's 980px min-width fits the ~1077px
   content column at 1400px, so the wrapper stays overflow:visible and the header pins to
   the VIEWPORT. top:4.5rem clears the admin's sticky .navbar-header; z-index 1 slides under
   its z-index 2. Any overflow value on the wrapper would make IT the scrollport and the
   header would never pin. Below 1399px the column is too narrow, so fall back to a
   horizontal scroll box with a static header.
   border-collapse also drops borders on sticky cells, so the header rule is an inset shadow. */
.favor-on .tblwrap{overflow:visible}
.favor-on table{border-collapse:collapse;width:100%;min-width:980px;font-size:13px}
.favor-on thead th{font-size:10px;letter-spacing:.09em;text-transform:uppercase;color:var(--faint);font-weight:700;text-align:left;padding:11px 12px;background:var(--head);position:sticky;top:4.5rem;z-index:1;box-shadow:inset 0 -1px 0 var(--line-2)}
.favor-on thead th:first-child{border-radius:8px 0 0 8px}
.favor-on thead th:last-child{border-radius:0 8px 8px 0}
@media(max-width:1399px){.favor-on .tblwrap{overflow-x:auto}.favor-on thead th{position:static}}
.favor-on td{padding:10px 12px;border-bottom:1px solid #f0ede8;vertical-align:middle}
.favor-on tbody tr{cursor:pointer}
.favor-on tbody tr:hover td{background:#faf9f6}
.favor-on tbody tr:last-child td{border-bottom:0}
.favor-on .sn{color:var(--faint);font-size:11.5px;width:34px}
.favor-on .mem{display:flex;align-items:center;gap:11px}
.favor-on .avw{position:relative;flex:none}
.favor-on .av{position:relative;width:34px;height:34px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:11.5px;font-weight:700;color:#fff;overflow:hidden}
.favor-on .av img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
/* The dot sits OUTSIDE .av (which is overflow:hidden for the photo) or it would be clipped. */
.favor-on .pdot{position:absolute;right:-3px;bottom:-3px;width:11px;height:11px;border-radius:50%;border:2.5px solid #fff;z-index:1}
.favor-on .pdot.on{background:var(--green)}
.favor-on .mi{min-width:0}
.favor-on .mi b{font-weight:700;color:var(--ink)}
.favor-on .mi small{display:block;color:var(--faint);font-size:11px}
.favor-on .mut{color:var(--faint)}
.favor-on .paid{font-size:8.5px;font-weight:800;letter-spacing:.05em;background:var(--amber-soft);color:var(--amber);border:1px solid #f0e2bd;border-radius:5px;padding:1px 6px;margin-left:6px;vertical-align:1px}
.favor-on .ch{font-size:9px;font-weight:800;letter-spacing:.04em;padding:3px 9px;border-radius:999px;white-space:nowrap;display:inline-block}
.favor-on .gf{background:var(--rose-soft);color:var(--rose)}
.favor-on .gm{background:var(--blue-soft);color:var(--blue)}
.favor-on .gu{background:var(--wash);color:var(--muted)}
.favor-on .s-active,.favor-on .s-approved{background:var(--green-soft);color:var(--green)}
.favor-on .s-inactive{background:var(--wash);color:var(--muted)}
.favor-on .s-ghost{background:var(--violet-soft);color:var(--violet)}
.favor-on .s-hidden{background:var(--blue-soft);color:var(--blue)}
.favor-on .s-deactivated{background:var(--amber-soft);color:var(--amber)}
.favor-on .s-deleted{background:var(--red-soft);color:var(--red)}
.favor-on .s-suspended{background:var(--black);color:#fff}
.favor-on .dev{color:var(--muted);font-size:12px;white-space:nowrap}
.favor-on .empty{padding:30px;text-align:center;color:var(--faint);font-size:13.5px}
.favor-on .legend{font-size:12px;color:var(--muted);margin-top:14px;border-top:1px solid var(--line);padding-top:12px;line-height:1.75}
.favor-on .legend b{color:var(--ink-2)}
@media(max-width:760px){.favor-on .phead h2{font-size:21px !important}}
`;

export default OnlineNow;
